export type ProgressProfile = {
  readonly weightKg?: number;
  readonly heightCm?: number;
  readonly age?: number;
  readonly gender?: string;
  readonly activityLevel?: string;
  readonly goalType?: string;
  readonly targetWeightKg?: number;
  readonly startingWeightKg?: number;
  readonly planStartedAt?: number;
  readonly planStartAt?: number;
};

const ACTIVITY_MULTIPLIERS: Record<string, number> = {
  sedentary: 1.2,
  light: 1.375,
  moderate: 1.55,
  active: 1.725,
  very_active: 1.9,
};

export function calculateBmr(profile: ProgressProfile): number | null {
  if (
    profile.weightKg === undefined ||
    profile.heightCm === undefined ||
    profile.age === undefined
  ) {
    return null;
  }
  const base = 10 * profile.weightKg + 6.25 * profile.heightCm - 5 * profile.age;
  if (profile.gender === "male") return Math.round(base + 5);
  if (profile.gender === "female") return Math.round(base - 161);
  return Math.round(base - 78);
}

export function calculateBmi(profile: ProgressProfile): number | null {
  if (profile.weightKg === undefined || profile.heightCm === undefined || profile.heightCm <= 0) {
    return null;
  }
  const heightM = profile.heightCm / 100;
  return Math.round((profile.weightKg / (heightM * heightM)) * 10) / 10;
}

export function calculateTdee(profile: ProgressProfile): number | null {
  const bmr = calculateBmr(profile);
  if (bmr === null) return null;
  return Math.round(bmr * (ACTIVITY_MULTIPLIERS[profile.activityLevel ?? ""] ?? 1.2));
}

export type PlanProgress = {
  readonly phaseNumber: number;
  readonly phaseDurationDays: number;
  readonly daysIntoPhase: number;
  readonly daysRemaining: number;
  readonly phaseProgressPercent: number;
  readonly projectedLossKg: number;
  readonly targetWeightKg: number | null;
  readonly estimatedDaysToTarget: number | null;
  readonly estimatedWeeksToTarget: number | null;
  readonly projectedTargetDate: number | null;
  readonly bmi: number | null;
  readonly bmrKcal: number | null;
  readonly tdeeKcal: number | null;
};

/**
 * @deprecated PlanProgressCard is now engine-driven (api.engine.getEngineState).
 * This local implementation remains only as the demo-mode/fallback path — demo
 * mode has no backend, so the pure functions below must stay pure and offline.
 */
export function calculatePlanProgress(
  profile: ProgressProfile,
  now = Date.now(),
): PlanProgress | null {
  const startingWeightKg = profile.startingWeightKg ?? profile.weightKg;
  const targetWeightKg = profile.targetWeightKg ?? null;
  if (startingWeightKg === undefined || targetWeightKg === null) return null;

  const phaseDurationDays = 60;
  const planStartAt = profile.planStartedAt ?? profile.planStartAt ?? now;
  const elapsedDays = Math.max(0, Math.floor((now - planStartAt) / 86_400_000));
  const phaseNumber = Math.floor(elapsedDays / phaseDurationDays) + 1;
  const daysIntoPhase = elapsedDays % phaseDurationDays;
  const daysRemaining = Math.max(0, phaseDurationDays - daysIntoPhase);
  const weeklyRateKg = profile.goalType === "gain" ? 0.35 : 0.75;
  const weightDelta = Math.abs(startingWeightKg - targetWeightKg);
  const estimatedDaysToTarget = weightDelta === 0
    ? 0
    : Math.ceil((weightDelta / weeklyRateKg) * 7);
  const estimatedWeeksToTarget = estimatedDaysToTarget === 0
    ? 0
    : Math.ceil(estimatedDaysToTarget / 7);
  const projectedLossKg = (weeklyRateKg / 7) * phaseDurationDays;

  return {
    phaseNumber,
    phaseDurationDays,
    daysIntoPhase,
    daysRemaining,
    phaseProgressPercent: Math.min(100, Math.round((daysIntoPhase / phaseDurationDays) * 100)),
    projectedLossKg: Math.round(projectedLossKg * 10) / 10,
    targetWeightKg,
    estimatedDaysToTarget,
    estimatedWeeksToTarget,
    projectedTargetDate: planStartAt + estimatedDaysToTarget * 86_400_000,
    bmi: calculateBmi(profile),
    bmrKcal: calculateBmr(profile),
    tdeeKcal: calculateTdee(profile),
  };
}
